import { useCallback, useEffect, useState } from "react";
import type { DocAsset, DocAssetType } from "@/lib/document/assetTypes";
import { listDocAssets, readImageDimensions, uploadDocAsset } from "@/lib/document/docAssetsClient";

export function useDocumentAssets(enabled: boolean) {
  const [assetTypeFilter, setAssetTypeFilter] = useState<DocAssetType | "all">("all");
  const [assets, setAssets] = useState<DocAsset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [uploadAssetType, setUploadAssetType] = useState<DocAssetType>("logo");
  const [uploadDisplayName, setUploadDisplayName] = useState("");

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    const r = await listDocAssets({
      assetType: assetTypeFilter === "all" ? undefined : assetTypeFilter,
      signMedia: true,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setAssets(r.data.assets);
    setSelectedAssetId((prev) => {
      if (prev && r.data.assets.some((a) => a.id === prev)) return prev;
      return null;
    });
  }, [enabled, assetTypeFilter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function submitUpload(file: File) {
    const displayName = uploadDisplayName.trim() || file.name.replace(/\.[^.]+$/, "") || "Asset";
    setUploadBusy(true);
    setError(null);
    setBanner(null);
    const dims = await readImageDimensions(file);
    const result = await uploadDocAsset({
      file,
      assetType: uploadAssetType,
      displayName,
      width: dims?.width ?? null,
      height: dims?.height ?? null,
    });
    setUploadBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setBanner(`Uploaded “${result.asset.display_name}”.`);
    setUploadDisplayName("");
    setSelectedAssetId(result.asset.id);
    await reload();
  }

  const selectedAsset = assets.find((a) => a.id === selectedAssetId) ?? null;

  return {
    assetTypeFilter,
    setAssetTypeFilter,
    assets,
    selectedAssetId,
    setSelectedAssetId,
    selectedAsset,
    loading,
    uploadBusy,
    error,
    banner,
    setBanner,
    uploadAssetType,
    setUploadAssetType,
    uploadDisplayName,
    setUploadDisplayName,
    submitUpload,
    reload,
  };
}
